import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { ApiClient, PermitTypeResponse } from '../../sdk/sdk';

@Component({
  selector: 'app-multi-permit-type-selector',
  templateUrl: './multi-permit-type-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgMultiLabelTemplateDirective,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgSelectComponent,
  ],
})
export class MultiPermitTypeSelectorComponent implements OnInit {
  private readonly apiClient = inject(ApiClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedIds = input<string[]>([]);
  readonly selectionChange = output<string[]>();

  readonly permitTypes = signal<PermitTypeResponse[]>([]);
  readonly allSelected = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly formControl = new FormControl<string[]>([]);
  readonly placeholder = 'Select Permit / Program...';

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(ids => this.selectionChange.emit(ids ?? []));

    effect(() => {
      const incoming = this.selectedIds();
      const current = this.formControl.value ?? [];
      if (!sameSet(incoming, current))
        this.formControl.setValue([...incoming], { emitEvent: false });
      this.updateAllSelectedFlag();
    });
  }

  ngOnInit(): void {
    this.apiClient.getPermitTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: types => {
          this.permitTypes.set([...types].sort((a, b) => a.ordinal - b.ordinal));
          // Re-apply the current selection now that items exist — ng-select needs
          // both [items] and a matching form-control value present at the same
          // time to render chips. If selectedIds() arrived first, the earlier
          // setValue() left ng-select with ids it couldn't render.
          const ids = this.selectedIds();
          if (ids.length > 0) {
            this.formControl.setValue([...ids], { emitEvent: false });
          }
          this.updateAllSelectedFlag();
        },
        error: err => {
          console.error('Failed to load permit types', err);
          this.loadError.set('Unable to load permit types. Try refreshing.');
        },
      });
  }

  onBlur(): void {
    this.formControl.markAsTouched();
  }

  onSelectionChange(_: unknown): void {
    this.updateAllSelectedFlag();
  }

  clearAll(): void {
    this.formControl.setValue([]);
  }

  private updateAllSelectedFlag(): void {
    const selected = this.formControl.value?.length ?? 0;
    const total = this.permitTypes().length;
    this.allSelected.set(selected > 0 && selected === total);
  }
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every(id => set.has(id));
}
